import React, { useCallback, useState } from 'react';
import { GeoJSON, useMapEvents } from 'react-leaflet';
import { size } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { setDisplayParam } from '../../actions';
import { mapDataKey } from '../../utils/map/MapCalculations';
import {
  featureFilter,
  featureFilterUnselectedTourLine,
  filterHitArea,
  filterOriginalLocation,
  isPedestrianRoute,
  isSegmentHidden,
  isUndesiredLine,
  isUndesiredPoint,
} from '../../utils/map/MapFeatureFilters';
import { onEachFeature } from '../../utils/map/MapTourLineUtils';
import {
  getStyle,
  getStyleHitArea,
  getStyleOriginalLocationLine,
  getStyleUnselected,
} from '../../utils/map/MapUtils';
import { AMPLITUDE_EVENTS, AmplitudeService } from '../../utils/amplitude';
import MapAnimatedTour from './MapAnimatedTour';
import { getSafeValue } from '../../utils/security';

const MapTourLines = ({
  mapData,
  user,
  display,
  routingRoutes,
  routingData,
  activateTour,
  tourData,
  originalLocation,
}) => {
  const solution = useSelector(({ solution: stateSolution }) => stateSolution);
  const tourPlanner = useSelector(
    ({ tourPlanner: stateTourPlanner }) => stateTourPlanner.value[solution.show],
  );
  const dispatch = useDispatch();
  const {
    vehicleProfiles: [{ fleetType }],
  } = tourPlanner || {};

  const [zoomLevel, setZoomLevel] = useState(5);
  const polylineCount = display.routeIds.reduce((total, routeId) => {
    return total + (routingData?.[routeId]?.polylineCount || 0);
  }, 0);
  const showAnimation = user.mapSettings.showTourDirectionAnimation;
  const showOverlaps = user.mapSettings.showOverlaps;
  const hideVisited = user.mapSettings.hideVisited;

  const mapEvents = useMapEvents({
    zoom: () => {
      setZoomLevel(mapEvents.getZoom());
    },
  });

  const onTourLineClick = useCallback(
    (feature) => {
      const routeId = feature.properties.routeId;
      if (routeId === undefined) return;
      const tour = getSafeValue(tourData.tours, routeId);
      activateTour(routeId, tour);
      AmplitudeService.log(AMPLITUDE_EVENTS.MAP_MARKER_ACTIVATE_TOUR);
    },
    [display, tourData, mapData, user, routingRoutes],
  );

  const onToggleHiddenSegment = useCallback(
    (feature) => {
      const { routeId, fromStop } = feature.properties;
      if (routeId === undefined || fromStop === undefined) return;
      const current = display.hiddenSegments || [];
      const isHidden = isSegmentHidden(feature, current);
      const hiddenSegments = isHidden
        ? current.filter((s) => !(s.routeId === routeId && s.fromStop === fromStop))
        : [...current, { routeId, fromStop }];
      dispatch(setDisplayParam({ hiddenSegments }));
    },
    [display.hiddenSegments, dispatch],
  );

  const filterLineFeatures = useCallback(
    (feature) => {
      return (
        featureFilter(feature, display, routingData) &&
        !isPedestrianRoute(feature) &&
        !isUndesiredPoint(feature)
      );
    },
    [display, routingData],
  );

  const filterLineFeaturesUnselected = useCallback(
    (feature) => {
      return featureFilterUnselectedTourLine(feature, display) && !isUndesiredPoint(feature);
    },
    [display],
  );

  const filterLineOriginalLocation = useCallback(
    (feature) => {
      return filterOriginalLocation(feature, display) && !isUndesiredPoint(feature);
    },
    [display],
  );

  const filterLinePedestrian = useCallback(
    (feature) => {
      return (
        featureFilter(feature, display, routingData) &&
        isPedestrianRoute(feature) &&
        !isUndesiredPoint(feature)
      );
    },
    [display, routingData],
  );

  const filterUndesired = useCallback(
    (feature) => {
      return isUndesiredLine(feature);
    },
    [display, routingData],
  );

  const onEachFeatureCb = useCallback(
    (feature, layer) => {
      onEachFeature(
        feature,
        layer,
        mapData,
        display,
        tourData,
        onTourLineClick,
        onToggleHiddenSegment,
      );
    },
    [mapData, display, tourData, onTourLineClick, onToggleHiddenSegment],
  );

  const makeRouting = useCallback(
    (feature) => {
      return getStyle(feature, user.mapSettings.gradientRoutes, fleetType, showOverlaps);
    },
    [user.mapSettings.gradientRoutes, fleetType, showOverlaps],
  );

  const filterHitAreaCallback = useCallback(
    (feature) => {
      return filterHitArea(feature, display, routingData);
    },
    [display, routingData],
  );

  const totalTours = tourData && tourData.tours && tourData.tours.length;
  const displayUnselected = tourData && routingRoutes && totalTours <= 100;

  return (
    <>
      {!hideVisited && displayUnselected && (
        <GeoJSON
          key={`${mapDataKey(mapData, user, display, routingRoutes)}-routing-unselected`}
          data={routingRoutes}
          style={getStyleUnselected}
          onEachFeature={onEachFeatureCb}
          filter={filterLineFeaturesUnselected}
        />
      )}
      <GeoJSON
        key={`${mapDataKey(mapData, user, display, routingRoutes)}-routing`}
        data={routingRoutes}
        style={makeRouting}
        onEachFeature={onEachFeatureCb}
        filter={filterLineFeatures}
      />
      <GeoJSON
        key={`${mapDataKey(mapData, user, display, routingRoutes)}-pedestrian-routing`}
        data={routingRoutes}
        style={makeRouting}
        onEachFeature={onEachFeatureCb}
        filter={filterLinePedestrian}
      />
      <GeoJSON
        key={`${mapDataKey(mapData, user, display, routingRoutes)}-undesired-routing`}
        data={routingRoutes}
        style={makeRouting}
        onEachFeature={onEachFeatureCb}
        filter={filterUndesired}
      />
      <GeoJSON
        key={`${mapDataKey(mapData, user, display, routingRoutes)}-hit-area`}
        data={routingRoutes}
        style={getStyleHitArea}
        onEachFeature={onEachFeatureCb}
        filter={filterHitAreaCallback}
      />
      {zoomLevel > 16 && (
        <GeoJSON
          key={`${mapDataKey(mapData, user, display, routingRoutes)}-original-location-line`}
          data={originalLocation}
          style={getStyleOriginalLocationLine}
          filter={filterLineOriginalLocation}
        />
      )}
      {!display.showUnassigned &&
        showAnimation &&
        size(display.routeIds) > 0 &&
        routingRoutes &&
        zoomLevel > 10 &&
        polylineCount <= 100000 && <MapAnimatedTour tour={routingRoutes} display={display} />}
    </>
  );
};

export default MapTourLines;
